import { useTranslation } from 'react-i18next';
import '../../../styles/pages/settings/settings.scss';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import React, { useEffect, useState, useRef } from 'react';
import { HOME_ROUTE } from '../../../lib/constants/route';
import SettingsIconSelectInput, {
  SettingsIconSelectInputRef,
} from '../../../ui/inputs/SettingsIconSelectInput';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  setIsTaraChatAnimationEnabled,
  setUserSettings,
  updateUserSetting,
  setAppThemeMode,
} from '../../../store/reducers/uiSlice';
import { setAppLanguage } from '../../../store/reducers/appSlice';
import get_user_settings_api_call, {
  update_user_settings_api_call,
  UserSettings,
} from '../../../lib/api/settings/user-settings-api-call';
import { applyThemeToApp as applyThemeToAppUtil } from '../../../lib/helper-functions/themeUtils';
import { questionSVG } from '../../../lib/allCustomSVGs';
import HELP_LINKS from '../../../lib/constants/helpLinks';

const TIMEZONE_OPTIONS = [
  { text: 'UTC-12:00 (Baker Island)', value: 'utc-12', icon: null },
  { text: 'UTC-11:00 (American Samoa)', value: 'utc-11', icon: null },
  { text: 'UTC-10:00 (Hawaii)', value: 'utc-10', icon: null },
  { text: 'UTC-09:30 (Marquesas Islands)', value: 'utc-9-30', icon: null },
  { text: 'UTC-09:00 (Alaska)', value: 'utc-9', icon: null },
  { text: 'UTC-08:00 (Pacific Time)', value: 'utc-8', icon: null },
  { text: 'UTC-07:00 (Mountain Time)', value: 'utc-7', icon: null },
  { text: 'UTC-06:00 (Central Time)', value: 'utc-6', icon: null },
  { text: 'UTC-05:00 (Eastern Time)', value: 'utc-5', icon: null },
  { text: 'UTC-04:00 (Atlantic Time)', value: 'utc-4', icon: null },
  { text: 'UTC-03:30 (Newfoundland)', value: 'utc-3-30', icon: null },
  { text: 'UTC-03:00 (Brasilia)', value: 'utc-3', icon: null },
  { text: 'UTC-02:00 (Mid-Atlantic)', value: 'utc-2', icon: null },
  { text: 'UTC-01:00 (Azores)', value: 'utc-1', icon: null },
  { text: 'UTC+00:00 (Greenwich Mean Time)', value: 'utc', icon: null },
  { text: 'UTC+01:00 (Central European Time)', value: 'utc+1', icon: null },
  { text: 'UTC+02:00 (Eastern European Time)', value: 'utc+2', icon: null },
  { text: 'UTC+03:00 (Moscow Time)', value: 'utc+3', icon: null },
  { text: 'UTC+03:30 (Iran Standard Time)', value: 'utc+3-30', icon: null },
  { text: 'UTC+04:00 (Gulf Standard Time)', value: 'utc+4', icon: null },
  { text: 'UTC+04:30 (Afghanistan Time)', value: 'utc+4-30', icon: null },
  { text: 'UTC+05:00 (Pakistan Standard Time)', value: 'utc+5', icon: null },
  { text: 'UTC+05:30 (India Standard Time)', value: 'utc+5-30', icon: null },
  { text: 'UTC+05:45 (Nepal Time)', value: 'utc+5-45', icon: null },
  { text: 'UTC+06:00 (Bangladesh Standard Time)', value: 'utc+6', icon: null },
  { text: 'UTC+06:30 (Myanmar Time)', value: 'utc+6-30', icon: null },
  { text: 'UTC+07:00 (Indochina Time)', value: 'utc+7', icon: null },
  { text: 'UTC+08:00 (China Standard Time)', value: 'utc+8', icon: null },
  { text: 'UTC+08:30 (North Korea Time)', value: 'utc+8-30', icon: null },
  { text: 'UTC+09:00 (Japan Standard Time)', value: 'utc+9', icon: null },
  {
    text: 'UTC+09:30 (Australian Central Time)',
    value: 'utc+9-30',
    icon: null,
  },
  { text: 'UTC+10:00 (Australian Eastern Time)', value: 'utc+10', icon: null },
  { text: 'UTC+10:30 (Lord Howe Time)', value: 'utc+10-30', icon: null },
  { text: 'UTC+11:00 (Solomon Islands Time)', value: 'utc+11', icon: null },
  { text: 'UTC+12:00 (New Zealand Time)', value: 'utc+12', icon: null },
  { text: 'UTC+12:45 (Chatham Time)', value: 'utc+12-45', icon: null },
  { text: 'UTC+13:00 (Tonga Time)', value: 'utc+13', icon: null },
  { text: 'UTC+14:00 (Line Islands Time)', value: 'utc+14', icon: null },
];

const LoadingSpinnerSmall = () => (
  <div
    style={{
      marginRight: '8px',
      width: '16px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        width: '16px',
        height: '16px',
        border: '2px solid #f3f3f3',
        borderTop: '2px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
  </div>
);

const ToggleSetting = ({
  title,
  description,
  isOn,
  isUpdating,
  isAnyUpdating,
  onToggle,
}: {
  title: string;
  description: string;
  isOn: boolean;
  isUpdating: boolean;
  isAnyUpdating: boolean;
  onToggle: () => void;
}) => (
  <div className='settingsList__content--item'>
    <div className='settingsList__content--item-title'>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
    <div
      className='settingsList__content--item-action'
      style={{ display: 'flex', alignItems: 'center' }}
    >
      {isUpdating && <LoadingSpinnerSmall />}
      <ToggleSwitch isOn={isOn} onClick={onToggle} disabled={isAnyUpdating} />
    </div>
  </div>
);

const DropdownSetting = React.forwardRef<
  SettingsIconSelectInputRef,
  {
    title: string;
    description: string;
    value: string;
    options: Array<{ text: string; value: string; icon: any }>;
    isUpdating: boolean;
    isAnyUpdating: boolean;
    onChange: (value: string) => void;
  }
>(
  (
    { title, description, value, options, isUpdating, isAnyUpdating, onChange },
    ref
  ) => (
    <div className='settingsList__content--item'>
      <div className='settingsList__content--item-title'>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div
        className='settingsList__content--item-action'
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <SettingsIconSelectInput
          ref={ref}
          filterOptions={options}
          value={value}
          onFilterChange={onChange}
          readonly={isAnyUpdating}
          loading={isUpdating}
        />
      </div>
    </div>
  )
);

const GeneralSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState('pending');
  const [theme, setTheme] = useState('system');
  const [timezone, setTimezone] = useState('utc');
  const [language, setLanguage] = useState('en');
  const [dataExportFormat, setDataExportFormat] = useState('json');
  const [emailNotification, setEmailNotification] = useState(false);
  const [pushNotification, setPushNotification] = useState(false);
  const [autoSaveChatHistory, setAutoSaveChatHistory] = useState(false);
  const [analyticsTracking, setAnalyticsTracking] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState<Set<string>>(
    new Set()
  );

  const dropdownRefs = {
    theme_mode: useRef<SettingsIconSelectInputRef>(null),
    time_zone: useRef<SettingsIconSelectInputRef>(null),
    language_code: useRef<SettingsIconSelectInputRef>(null),
    data_export_format: useRef<SettingsIconSelectInputRef>(null),
  };

  const isTaraChatAnimationEnabled = useSelector(
    (state: RootState) => state.UI.isTaraChatAnimationEnabled
  );
  const userSettings = useSelector((state: RootState) => state.UI.userSettings);
  const currentTheme = useSelector((state: RootState) => state.UI.theme);

  useEffect(() => {
    fetchUserSettings();
  }, []);

  useEffect(() => {
    if (
      theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia
    ) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleSystemThemeChange = () => {
        applyThemeToApp('system');
      };

      mediaQuery.addEventListener('change', handleSystemThemeChange);

      return () => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      };
    }
  }, [theme, currentTheme.dark]);

  const fetchUserSettings = async () => {
    try {
      setLoading('pending');
      const settings = await get_user_settings_api_call();

      if (settings) {
        dispatch(setUserSettings(settings));

        if (settings.theme_mode) {
          setTheme(settings.theme_mode);
          applyThemeToApp(settings.theme_mode);
        }
        if (settings.time_zone) setTimezone(settings.time_zone);
        if (settings.language_code) setLanguage(settings.language_code);
        if (settings.data_export_format)
          setDataExportFormat(settings.data_export_format);
        if (settings.email_notification !== undefined)
          setEmailNotification(settings.email_notification);
        if (settings.push_notification !== undefined)
          setPushNotification(settings.push_notification);
        if (settings.auto_save_chat_history !== undefined)
          setAutoSaveChatHistory(settings.auto_save_chat_history);
        if (settings.analytics_tracking !== undefined)
          setAnalyticsTracking(settings.analytics_tracking);

        setLoading('success');
      } else {
        setLoading('error');
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
      setLoading('error');
    }
  };

  const applyThemeToApp = (themeMode: string) =>
    applyThemeToAppUtil(themeMode as any, currentTheme.dark, () =>
      dispatch(setAppThemeMode())
    );

  const updateSetting = async (key: keyof UserSettings, value: any) => {
    setUpdatingSettings((prev) => new Set(prev).add(key));

    try {
      const updatedSettings = {
        ...userSettings,
        [key]: value,
      };

      const success = await update_user_settings_api_call(updatedSettings);

      if (success) {
        dispatch(updateUserSetting({ key, value }));

        switch (key) {
          case 'email_notification':
            setEmailNotification(value);
            break;
          case 'push_notification':
            setPushNotification(value);
            break;
          case 'chat_animation':
            dispatch(setIsTaraChatAnimationEnabled(value));
            break;
          case 'theme_mode':
            applyThemeToApp(value);
            break;
          case 'language_code':
            dispatch(setAppLanguage(value));
            break;
          case 'auto_save_chat_history':
            setAutoSaveChatHistory(value);
            break;
          case 'analytics_tracking':
            setAnalyticsTracking(value);
            break;
        }
      }
    } catch (error) {
      console.error('Error updating setting:', error);
    } finally {
      setUpdatingSettings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });

      dropdownRefs[key as keyof typeof dropdownRefs]?.current?.closeMenu();
    }
  };

  const isSettingUpdating = (key: string) => updatingSettings.has(key);
  const isAnySettingUpdating = updatingSettings.size > 0;

  const handleDropdownChange = (key: keyof UserSettings, value: any) => {
    const stateSetters = {
      theme_mode: setTheme,
      time_zone: setTimezone,
      language_code: setLanguage,
      data_export_format: setDataExportFormat,
    };

    stateSetters[key as keyof typeof stateSetters]?.(value);

    updateSetting(key, value);
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <SubPageInnerContentHolder
        title={t('GeneralSettings')}
        // subTitle={t('ManageGeneralSettings')}
        backBtnLink={HOME_ROUTE}
        /* ------Help Button------ */
        mainButtonType='white'
        mainBtnTitle={t('Help')}
        mainBtnIcon={questionSVG}
        mainBtnLoading={false}
        mainBtnLink={HELP_LINKS.settings.general}
        isMainBtnLinkExternal={true}
      >
        <>
          {loading === 'pending' && <LoadingSpinner />}
          {loading === 'error' && (
            <NoData
              title={t('ErrorFetchingData')}
              subTitle={t('SomethingWentWrong-msg')}
            />
          )}
          {loading === 'success' && (
            <div className='settingsList settings_mobile-only-top-margin'>
              <div className='settingsList__title'>
                <h2>{t('GeneralSettings')}</h2>
                <p>{t('ConfigureYourGeneralPreferences')}</p>
              </div>

              {/* <div
                className='settingsList__content--divider'
                style={{ marginBottom: '3rem' }}
              ></div> */}

              <div
                style={{ marginTop: '2rem' }}
                className='settingsList__content'
              >
                {/* <ToggleSetting
                  title={t('EmailNotifications')}
                  description={t('ReceiveEmailUpdatesAboutYourAccount')}
                  isOn={emailNotification}
                  isUpdating={isSettingUpdating('email_notification')}
                  isAnyUpdating={isAnySettingUpdating}
                  onToggle={() =>
                    updateSetting('email_notification', !emailNotification)
                  }
                />
                <div className='settingsList__content--divider'></div> */}

                {/* <ToggleSetting
                  title={t('PushNotifications')}
                  description={t('ReceivePushNotificationsInYourBrowser')}
                  isOn={pushNotification}
                  isUpdating={isSettingUpdating('push_notification')}
                  isAnyUpdating={isAnySettingUpdating}
                  onToggle={() =>
                    updateSetting('push_notification', !pushNotification)
                  }
                />
                <div className='settingsList__content--divider'></div> */}

                <ToggleSetting
                  title={t('ChatAnimation')}
                  description={t('EnableChatAnimation')}
                  isOn={isTaraChatAnimationEnabled}
                  isUpdating={isSettingUpdating('chat_animation')}
                  isAnyUpdating={isAnySettingUpdating}
                  onToggle={() =>
                    updateSetting('chat_animation', !isTaraChatAnimationEnabled)
                  }
                />
                <div className='settingsList__content--divider'></div>

                <DropdownSetting
                  title={t('Theme')}
                  description={t('ChooseYourPreferredTheme')}
                  value={theme}
                  options={[
                    { text: t('System'), value: 'system', icon: null },
                    { text: t('Light'), value: 'light', icon: null },
                    { text: t('Dark'), value: 'dark', icon: null },
                  ]}
                  isUpdating={isSettingUpdating('theme_mode')}
                  isAnyUpdating={isAnySettingUpdating}
                  onChange={(val) => handleDropdownChange('theme_mode', val)}
                  ref={dropdownRefs.theme_mode}
                />
                {/* <div className='settingsList__content--divider'></div> */}

                {/* <ToggleSetting
                  title={t('AutoSaveChatHistory')}
                  description={t('AutomaticallySaveYourChatConversations')}
                  isOn={autoSaveChatHistory}
                  isUpdating={isSettingUpdating('auto_save_chat_history')}
                  isAnyUpdating={isAnySettingUpdating}
                  onToggle={() =>
                    updateSetting(
                      'auto_save_chat_history',
                      !autoSaveChatHistory
                    )
                  }
                />
                <div className='settingsList__content--divider'></div> */}

                {/* <ToggleSetting
                  title={t('AnalyticsTracking')}
                  description={t(
                    'AllowAnonymousUsageAnalyticsToImproveOurService'
                  )}
                  isOn={analyticsTracking}
                  isUpdating={isSettingUpdating('analytics_tracking')}
                  isAnyUpdating={isAnySettingUpdating}
                  onToggle={() =>
                    updateSetting('analytics_tracking', !analyticsTracking)
                  }
                />
                <div className='settingsList__content--divider'></div> */}
                <div className='settingsList__content--divider'></div>
                <DropdownSetting
                  title={t('Language')}
                  description={t('ChooseYourPreferredLanguage')}
                  value={language}
                  options={[
                    { text: t('English'), value: 'en', icon: null },
                    { text: t('Dutch'), value: 'nl', icon: null },
                    { text: t('Spanish'), value: 'es', icon: null },
                    { text: t('German'), value: 'de', icon: null },
                    { text: t('French'), value: 'fr', icon: null },
                    { text: t('Italian'), value: 'it', icon: null },
                    { text: t('Portuguese'), value: 'pt', icon: null },
                  ]}
                  isUpdating={isSettingUpdating('language_code')}
                  isAnyUpdating={isAnySettingUpdating}
                  onChange={(val) => handleDropdownChange('language_code', val)}
                  ref={dropdownRefs.language_code}
                />
                <div className='settingsList__content--divider'></div>

                <DropdownSetting
                  title={t('Timezone')}
                  description={t('ChooseYourPreferredTimezone')}
                  value={timezone}
                  options={TIMEZONE_OPTIONS}
                  isUpdating={isSettingUpdating('time_zone')}
                  isAnyUpdating={isAnySettingUpdating}
                  onChange={(val) => handleDropdownChange('time_zone', val)}
                  ref={dropdownRefs.time_zone}
                />
                {/* <div className='settingsList__content--divider'></div> */}

                {/* <div className='settingsList__content--itemSelect'>
                  <SettingsIconSelectInput
                    ref={dropdownRefs.data_export_format}
                    filterOptions={[
                      { text: t('JSON'), value: 'json', icon: null },
                      { text: t('CSV'), value: 'csv', icon: null },
                      { text: t('Excel'), value: 'excel', icon: null },
                    ]}
                    value={dataExportFormat}
                    onFilterChange={(val) =>
                      handleDropdownChange('data_export_format', val)
                    }
                    title={t('DataExportFormat')}
                    readonly={isAnySettingUpdating}
                    loading={isSettingUpdating('data_export_format')}
                  />
                </div> */}
              </div>
            </div>
          )}
        </>
      </SubPageInnerContentHolder>
    </>
  );
};

export default GeneralSettings;
